import { z } from 'zod';

/**
 * Typed, validated frontend environment for a Hostesa app.
 *
 * Values come from Vite's `import.meta.env` (statically replaced at build time).
 * Validation runs once via {@link loadAppConfig}; a missing or malformed required
 * variable fails fast with a clear error rather than surfacing later as a runtime
 * `undefined`. This is the only place env access is allowed — feature code reads
 * the resolved {@link AppConfig}, never `import.meta.env` directly.
 */
const envSchema = z.object({
  /** Which app build this is — drives Sentry project + release tagging. */
  VITE_APP: z.enum(['platform', 'backoffice']),
  /** Deployment environment. */
  VITE_ENVIRONMENT: z.enum(['local', 'preview', 'staging', 'production']).default('local'),
  /** Backend REST base origin, e.g. http://localhost:3000 (no trailing slash). */
  VITE_API_BASE_URL: z.string().url(),
  /** Socket.IO realtime origin, e.g. http://localhost:3002. */
  VITE_REALTIME_URL: z.string().url(),
  /** Release identifier (git SHA / CI build id) embedded for support + Sentry. */
  VITE_RELEASE_ID: z.string().default('dev'),
  /** Per-app Sentry DSN. Optional in local/dev. */
  VITE_SENTRY_DSN: z.string().optional(),
  /** Default UI locale; structure also supports bg/de/fr. */
  VITE_DEFAULT_LOCALE: z.enum(['en', 'bg', 'de', 'fr']).default('en'),
});

export type RawEnv = z.infer<typeof envSchema>;

export interface AppConfig {
  readonly app: 'platform' | 'backoffice';
  readonly environment: 'local' | 'preview' | 'staging' | 'production';
  readonly isProduction: boolean;
  readonly apiBaseUrl: string;
  readonly realtimeUrl: string;
  readonly releaseId: string;
  readonly sentryDsn: string | undefined;
  readonly defaultLocale: 'en' | 'bg' | 'de' | 'fr';
}

const stripTrailingSlash = (url: string): string => url.replace(/\/$/, '');

/**
 * Validate and freeze the app configuration. Call once at app bootstrap.
 * @throws if any required variable is missing or malformed.
 */
export function loadAppConfig(source: Record<string, unknown>): AppConfig {
  const parsed = envSchema.safeParse(source);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n');
    throw new Error(
      `Invalid frontend environment configuration:\n${issues}\n` +
        'Check your .env / deployment environment variables.',
    );
  }
  const env = parsed.data;
  return Object.freeze({
    app: env.VITE_APP,
    environment: env.VITE_ENVIRONMENT,
    isProduction: env.VITE_ENVIRONMENT === 'production',
    apiBaseUrl: stripTrailingSlash(env.VITE_API_BASE_URL),
    realtimeUrl: stripTrailingSlash(env.VITE_REALTIME_URL),
    releaseId: env.VITE_RELEASE_ID,
    sentryDsn: env.VITE_SENTRY_DSN,
    defaultLocale: env.VITE_DEFAULT_LOCALE,
  });
}

export { envSchema };
